
import { defineComponent, onMounted, reactive } from 'vue'

// @components
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import TripsTMSActions from '@/modules/TMSModule/components/TripsTMSActions/index.vue'

// @module components
import TripsTMSFilters from '@/modules/TMSModule/components/TripsTMSFilters/index.vue'
import TripsTMSRow from '@/modules/TMSModule/components/TripsTMSRow/index.vue'

// @types
import { AddTtnTMSPayload, GetTripsTMSParams, TripTMS, TtnTMS } from '@/new-services/types/tms'

// @new-services
import tmsAPI from '@/new-services/tms.service'

// @composable
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'
import useActions from '@/common/composable/useActions'
import useAccount from '@/common/composable/useAccount'

const TABLE_COLUMNS = [
    {
        key: 'index',
        name: '№',
    },
    {
        key: 'tripNumber',
        name: 'Номер рейса',
    },
    {
        key: 'paymentBySupplierNumber',
        name: 'Номер РПП'
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'company',
        name: 'Перевозчик',
    },
    {
        key: 'createdAt',
        name: 'Дата создания',
    },
    {
        key: 'expectedDate',
        name: 'Дата прибытия план',
    },
    {
        key: 'arrivalDate',
        name: 'Дата прибытия факт',
    },
    {
        key: 'transportType',
        name: 'Тип перевозки',
    },
    {
        key: 'totalWeight',
        name: 'Вес (кг)',
    },
    {
        key: 'totalCubature',
        name: 'Кубатура',
    },
    {
        key: 'status',
        name: 'Статус',
    },
    {
        key: 'comment',
        name: 'Комментарий',
    },
    {
        key: 'logistName',
        name: 'Логист',
    },
    {
        key: 'costOfTransportation',
        name: 'Стоимость',
    },
    {
        key: 'totalVolumeWeight',
        name: 'Объемный вес',
    },
    {
        key: 'departureDate',
        name: 'Дата отправления',
    },
    {
        key: 'isProject',
        name: 'Проект',
    },
]

export default defineComponent({
    components: {
        TripsTMSFilters,
        'trips-tms-row': TripsTMSRow,
        NewTable,
        NewButton,
        Loader,
        NewLimit,
        NewPagination,
        'trips-tms-actions': TripsTMSActions,
    },
    setup() {
        const { saveFile } = useFile()
        const notifications = useNotifications()
        const { actions_state, startCreate } = useActions<TtnTMS>()
        const { havePermissions } = useAccount()

        const state = reactive({
            list: [] as TripTMS[],
            is_excel_loading: false,
            is_loading: false,
            is_failed: false,
            filters: {
                tripNumber: null as string | null,
                fromCityId: null as number | null,
                toCityId: null as number | null,
                companyId: null as number | null,
                dateFrom: null as string | null,
                dateTo: null as string | null,
                statusIds: [] as number[],
                logistId: null as number | null,
                transportTypeIds: [] as number[],
                commentIds: [] as number[],
                isProject: null as number | null,
                isAdditionalLoad: null as number | null,
            },
            pagination: {
                page: 1,
                limit: 50,
                total_pages: 1,
                total_items: 20,
            },
        })

        //

        const loadTrips = async () => {
            try {
                state.is_loading = true

                const params: GetTripsTMSParams = {
                    page: state.pagination.page,
                    limit: state.pagination.limit,
                    ...state.filters,
                }

                const response = await tmsAPI.getTrips(params)
                state.list = response.data.data
                state.pagination.total_items = response.data.meta.total
                state.pagination.total_pages = response.data.meta.last_page

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersChanged = (filters: GetTripsTMSParams) => {
            state.filters.tripNumber = filters.tripNumber as string | null
            state.filters.fromCityId = filters.fromCityId as number | null
            state.filters.toCityId = filters.toCityId as number | null
            state.filters.companyId = filters.companyId as number | null
            state.filters.dateFrom = filters.dateFrom as string | null
            state.filters.dateTo = filters.dateTo as string | null
            state.filters.statusIds = filters.statusIds as number[]
            state.filters.logistId = filters.logistId as number | null
            state.filters.transportTypeIds = filters.transportTypeIds as number[]
            state.filters.commentIds = filters.commentIds as number[]
            state.filters.isProject = filters.isProject ? 1 : null
            state.filters.isAdditionalLoad = filters.isAdditionalLoad ? 1 : null

            state.pagination.page = 1

            loadTrips()
        }

        const onFiltersReset = () => {
            state.filters.tripNumber = null
            state.filters.fromCityId = null
            state.filters.toCityId = null
            state.filters.companyId = null
            state.filters.dateFrom = null
            state.filters.dateTo = null
            state.filters.statusIds = []
            state.filters.logistId = null
            state.filters.transportTypeIds = []
            state.filters.commentIds = []
            state.filters.isProject = null
            state.filters.isAdditionalLoad = null

            state.pagination.page = 1

            loadTrips()
        }

        const downloadReportTrips = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    ...state.filters,
                }

                const response = await tmsAPI.downloadReportTrips(params)
                saveFile(response.data, `Отчет по рейсам`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const createTrip = async (payload: AddTtnTMSPayload) => {
            try {
                actions_state.is_actions_loading = true

                await tmsAPI.addTripTMS(payload)
                await loadTrips()

                actions_state.is_actions_opened = false
                notifications.notify({ title: 'Рейс успешно создан!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании рейса произошла ошибка' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const toggleCreateTrip = () => {
            actions_state.is_actions_opened = !actions_state.is_actions_opened
        }

        const onLimitChange = (new_limit: number) => {
            state.pagination.limit = new_limit
            loadTrips()
        }

        const onChangePage = (new_page: number) => {
            state.pagination.page = new_page
            loadTrips()
        }

        //

        onMounted(loadTrips)

        //

        return {
            state,
            actions_state,
            //
            startCreate,
            onFiltersChanged,
            onFiltersReset,
            onLimitChange,
            onChangePage,
            loadTrips,
            downloadReportTrips,
            toggleCreateTrip,
            createTrip,
            havePermissions,
            //
            TABLE_COLUMNS,
        }
    },
})
